import { set } from "date-fns";
import { useEffect, useState } from "react";
import { informeApi } from "@/redux/api/informeApi";
import { ICarrito, IInforme, IUser } from "@/redux/models";
import { modalsTypes } from "../components/Modals";
import dayjs from "dayjs";
import { useAppSelector } from "@/redux/store";

export enum ViewAs {
    USER = 'USER',
    ADMIN = 'ADMIN'
}

export type filtersCarpeta = {
    page: number,
    perPage: number,
    estado?: string, // Pendiente, En proceso, Informada
    usuario?: IUser,
    fechaDesde?: Date,
    fechaHasta?: Date,
    estaLiquidado?: boolean
    estaPago?: boolean
    carpetaId?: number
    companyId?: number
}

export interface useTablaCarritoV2Props {
    carpetas: ICarrito[];
    setCarpetas: (carpeta: ICarrito[]) => void;
    isCarpetaSelected: (carrito: ICarrito) => boolean;
    borrarCarpeta: (carrito: ICarrito) => void;
    carpetasBeingDeleted: number[];
    /**
     * 
     * @param carrito 
        * Modifica una carpeta en el carrito localmente
     */
    modificarCarpeta: (carrito: ICarrito) => void;
    modificarCarpetaById: (carrito: Partial<ICarrito> & { idCarrito: number }) => void;
    agregarCarpeta: (carrito: ICarrito) => void;
    refetchCarpetas: () => void;

    informes: Record<number, IInforme[]>;
    borrarInforme: (informe: IInforme) => void;
    /**
     * 
     * @param informe 
        * Modifica un informe en el carrito localmente
     */
    modificarInforme: (informe: IInforme) => void;

    loading: boolean;

    page: number;
    perPage: number;
    total: number;

    isForEmpresa: boolean;

    filter: filtersCarpeta;
    setFilter: (filter: filtersCarpeta) => void;

    modal: { type: modalsTypes | '', data: ICarrito | undefined | IInforme | any };
    setModal: (modal: modalsTypes | '', data?: ICarrito | undefined | IInforme | any) => void;

    selectedCarritos: ICarrito[];
    setSelectedCarritos: (carritos: ICarrito[]) => void;
    toggleSelectedCarrito: (carrito: ICarrito) => void;

    nextPage: () => void;
    prevPage: () => void;
    setPage: (page: number) => void;
    changePerPage: (perPage: number) => void;
    search: (filters?: filtersCarpeta) => void;

    viewAs: ViewAs;
}

export const useTablaCarritoV2 = (viewAs: ViewAs, isForEmpresa = false): useTablaCarritoV2Props => {
    const [obtenerCarritos] = informeApi.endpoints.obtenerCarritosPaginados.useLazyQuery()
    const user = useAppSelector((state) => state.userState.user) as IUser;
    const [carpetas, setCarpetas] = useState<ICarrito[]>([]);
    const [informes, setInformes] = useState<Record<number, IInforme[]>>({})
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [modalOpen, setModalOpen] = useState<{ type: modalsTypes | '', data: ICarrito | undefined | IInforme }>({ type: '', data: undefined })
    const [carpetasBeingDeleted, setCarpetasBeingDeleted] = useState<number[]>([])
    const [filter, setFilter] = useState<filtersCarpeta>({
        page: 1,
        perPage: viewAs === ViewAs.ADMIN ? 50 : 5,
        usuario: viewAs === ViewAs.USER ? user : undefined
    })

    const [selectedCarritos, setSelectedCarritos] = useState<ICarrito[]>([])
    const isSelected = (carrito: ICarrito) => selectedCarritos.includes(carrito);

    const obtenerCarpetas = ({ page, perPage, estado, usuario, fechaDesde, fechaHasta, companyId }: filtersCarpeta) => {
        setLoading(true)
        obtenerCarritos({
            limit: perPage,
            page: page,
            estado,
            userId: Number(usuario?.id),
            fechaDesde: fechaDesde && dayjs(fechaDesde).format('YYYY-MM-DD'),
            fechaHasta: fechaHasta && dayjs(fechaHasta).format('YYYY-MM-DD'),
            liquidado: filter.estaLiquidado,
            pagado: filter.estaPago,
            carpetaId: filter.carpetaId,
            companyId,
        }).unwrap().then((res: any) => {
            const { page: carpetas, totalPages: totalCarpetas }: { page: ICarrito[], totalPages: number } = res.data
            const informePorCarritoId = {} as Record<number, IInforme[]>
            const nuevasCarpetas = carpetas.map((c: any) => ({ ...c, informes: [] }))
            for (const c of carpetas) {

                informePorCarritoId[c.idCarrito] = c.informes
            }

            setCarpetas(nuevasCarpetas)
            setInformes(informePorCarritoId)
            setTotal(totalCarpetas)
        }).catch(console.error)

            .finally(() => setLoading(false))
    }

    const handleSearch = (newFilters?: filtersCarpeta) => {
        setSelectedCarritos([])
        obtenerCarpetas(newFilters || filter)
    }


    const handleToggleSelectedCarrito = (carrito: ICarrito) => {
        if (selectedCarritos.includes(carrito)) {
            setSelectedCarritos(selectedCarritos.filter(c => c !== carrito))
        } else {
            setSelectedCarritos([...selectedCarritos, carrito])
        }
    }

    const handleAgregarCarpeta = (carrito: ICarrito) => {
        const informes = { ...carrito.informes }
        carrito.informes = []
        setCarpetas([...carpetas, carrito])
        setInformes({ ...informes, [carrito.idCarrito]: informes })
    }

    const handleBorrarCarpeta = (carrito: ICarrito) => {
        // Primero marcar como "siendo eliminada" para la animación
        setCarpetasBeingDeleted(prev => [...prev, carrito.idCarrito]);
        
        // Luego eliminar después de un delay para permitir la animación
        setTimeout(() => {
            setCarpetas(carpetas.filter(c => c.idCarrito !== carrito.idCarrito))
            setInformes({ ...informes, [carrito.idCarrito]: [] })
            setCarpetasBeingDeleted(prev => prev.filter(id => id !== carrito.idCarrito));
        }, 600); // Tiempo suficiente para la animación
    }

    const handleModificarCarpeta = (carrito: ICarrito) => {
        const nuevasCarpetas = carpetas.map(c => c.idCarrito === carrito.idCarrito ? carrito : c)
        setCarpetas(nuevasCarpetas)
    }

    const handleModificarCarpetaById = (carrito: Partial<ICarrito> & { idCarrito: number }) => {
        const nuevasCarpetas = carpetas.map(c => c.idCarrito === carrito.idCarrito ? { ...c, ...carrito } : c)
        setCarpetas(nuevasCarpetas)
    }

    const handleEliminarInforme = (informe: IInforme) => {
        const idCarrito = informe.idCarrito
        const idInforme = informe.id
        const nuevosInformes = informes[idCarrito].filter(i => i.id !== idInforme)
        setInformes({ ...informes, [idCarrito]: nuevosInformes })
        if (nuevosInformes.length === 0) {
            setCarpetas(carpetas.filter(c => c.idCarrito !== idCarrito))
        }
    }


    /**
     * 
     * @param informe 
        * Modifica un informe en el carrito localmente
     */

    const handleModificarInforme = (informe: IInforme) => {
        const { idCarrito } = informe
        const nuevosInformes = informes[idCarrito].map(i => i.id === informe.id ? informe : i)
        setInformes({ ...informes, [idCarrito]: nuevosInformes })
    }

    const updateFiltersAndSearch = (newFilters: filtersCarpeta) => {
        setFilter(newFilters)
        handleSearch(newFilters)
    }

    return {
        page: filter.page,
        perPage: filter.perPage,
        total,
        carpetas,
        isCarpetaSelected: isSelected,
        setCarpetas,
        selectedCarritos,
        toggleSelectedCarrito: handleToggleSelectedCarrito,
        setSelectedCarritos,
        loading,
        isForEmpresa: viewAs === ViewAs.ADMIN && isForEmpresa,
        nextPage: () => updateFiltersAndSearch({ ...filter, page: filter.page + 1, perPage: filter.perPage }),
        prevPage: () => updateFiltersAndSearch({ ...filter, page: filter.page - 1, perPage: filter.perPage }),
        setPage: (page: number) => updateFiltersAndSearch({ page, perPage: filter.perPage }),
        changePerPage: (perPage: number) => updateFiltersAndSearch({ ...filter, page: 1, perPage }),
        modal: modalOpen,
        setModal: (type: modalsTypes | '', data?: ICarrito | undefined | IInforme) => setModalOpen({ type, data }),
        informes,
        borrarInforme: handleEliminarInforme,
        modificarInforme: handleModificarInforme,
        borrarCarpeta: handleBorrarCarpeta,
        carpetasBeingDeleted,
        modificarCarpeta: handleModificarCarpeta,
        modificarCarpetaById: handleModificarCarpetaById,
        agregarCarpeta: handleAgregarCarpeta,
        refetchCarpetas: () => obtenerCarpetas(filter),
        filter,
        setFilter,
        search: handleSearch,
        viewAs

    }

}

